import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Invoice, Customer, Vehicle, Appointment } from '@/types';
import { PaymentOutput } from '@/types/payment.types';
import { formatCurrency } from '@/lib/utils';
import { format } from 'date-fns';

// Company information
const COMPANY_INFO = {
  name: 'WeClean Car Wash',
  tagline: 'We come to you',
  phone: '+974 7090 8373',
  website: 'www.weclean.com.qa',
  email: 'info@weclean.com.qa',
};

interface GenerateInvoicePdfParams {
  invoice: Invoice;
  customer: Customer;
  vehicle: Vehicle;
  appointment?: Appointment;
}

export function generateInvoicePdf({
  invoice,
  customer,
  vehicle,
  appointment,
}: GenerateInvoicePdfParams): void {
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Colors
    const black = [0, 0, 0];
    const darkGray = [64, 64, 64];

    // Set font
    doc.setFont('helvetica');

    // Logo placeholder (black square in top right)
    const logoSize = 25;
    const logoX = pageWidth - margin - logoSize;
    const logoY = margin;
    doc.setFillColor(...black);
    doc.rect(logoX, logoY, logoSize, logoSize, 'F');
    
    // Pink flower icon placeholder (four-petal flower simplified)
    // Draw a simple pink circle in the center of the black square
    doc.setFillColor(255, 192, 203); // Pink color
    const centerX = logoX + logoSize / 2;
    const centerY = logoY + logoSize / 2;
    const flowerRadius = logoSize / 6;
    doc.circle(centerX, centerY, flowerRadius, 'F');

    // Invoice title (top left)
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('Invoice', margin, yPosition + 12);

    // Invoice details (below title)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    
    yPosition += 18;
    doc.text(`Invoice number: ${invoice.invoiceNumber}`, margin, yPosition);
    
    yPosition += 6;
    const issueDate = format(new Date(invoice.date), 'MMMM dd, yyyy');
    doc.text(`Date of issue: ${issueDate}`, margin, yPosition);
    
    yPosition += 6;
    // Use invoice date as due date (can be customized later)
    doc.text(`Date due: ${issueDate}`, margin, yPosition);

    // Company information (left side, below invoice details)
    yPosition += 18;
    const companyStartY = yPosition;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text(COMPANY_INFO.name, margin, yPosition);
    
    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text(COMPANY_INFO.tagline, margin, yPosition);
    
    yPosition += 6;
    doc.text(COMPANY_INFO.phone, margin, yPosition);
    
    yPosition += 6;
    doc.text(COMPANY_INFO.email, margin, yPosition);
    
    yPosition += 6;
    doc.text(COMPANY_INFO.website, margin, yPosition);

    // Bill To section (right side, aligned with company info)
    const billToX = pageWidth / 2 + 5;
    yPosition = companyStartY;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text('Bill to:', billToX, yPosition);
    
    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...black);
    doc.text(customer.name, billToX, yPosition);
    
    yPosition += 6;
    doc.setFontSize(10);
    doc.setTextColor(...darkGray);
    if (customer.city) {
      doc.text(customer.city, billToX, yPosition);
      yPosition += 6;
    }
    if (customer.email) {
      doc.text(customer.email, billToX, yPosition);
      yPosition += 6;
    }

    // Amount due summary (large, bold - prominent display)
    yPosition += 25;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    const amountDueText = `${formatCurrency(invoice.total)} due ${issueDate}`;
    doc.text(amountDueText, margin, yPosition);

    // Line items table
    yPosition += 15;
    
    // Prepare table data
    const tableData = invoice.items.map((item) => {
      // Build description - first line is the item name
      const descriptionLines: string[] = [item.name];
      
      // Add description if available
      if (item.description) {
        descriptionLines.push(item.description);
      }
      
      // Add service period for subscriptions
      if (appointment && appointment.packageType === 'subscription') {
        const startDate = format(new Date(appointment.date), 'MMM dd');
        // Calculate end date (assuming monthly subscription)
        const endDate = new Date(appointment.date);
        endDate.setMonth(endDate.getMonth() + 1);
        const endDateFormatted = format(endDate, 'MMM dd, yyyy');
        descriptionLines.push(`Service Period: ${startDate} - ${endDateFormatted}`);
      }
      
      return [
        descriptionLines.join('\n'),
        item.quantity.toString(),
        formatCurrency(item.unitPrice),
        formatCurrency(item.total),
      ];
    });

    // Create table
    autoTable(doc, {
      startY: yPosition + 8,
      head: [['Description', 'Qty', 'Unit price', 'Amount']],
      body: tableData,
      theme: 'plain',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontStyle: 'bold',
        fontSize: 10,
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
        halign: 'left',
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontSize: 10,
        lineColor: [240, 240, 240],
        lineWidth: 0.3,
      },
      styles: {
        cellPadding: { top: 6, bottom: 6, left: 5, right: 5 },
        fontSize: 10,
        font: 'helvetica',
        overflow: 'linebreak',
        cellWidth: 'wrap',
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left', valign: 'top' },
        1: { cellWidth: 18, halign: 'center', valign: 'middle' },
        2: { cellWidth: 32, halign: 'right', valign: 'middle' },
        3: { cellWidth: 32, halign: 'right', valign: 'middle' },
      },
      margin: { left: margin, right: margin },
      tableWidth: 'auto',
    });

    // Get final Y position after table
    const finalY = (doc as any).lastAutoTable.finalY || yPosition + 50;

    // Total charges breakdown (right-aligned)
    let totalY = finalY + 12;
    const labelX = pageWidth - margin - 50;
    const valueX = pageWidth - margin;
    
    // Subtotal
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text('Subtotal:', labelX, totalY, { align: 'right' });
    doc.setTextColor(...black);
    doc.text(formatCurrency(invoice.subtotal), valueX, totalY, { align: 'right' });
    
    // Discount (if any)
    if (invoice.discount > 0) {
      totalY += 6;
      doc.setTextColor(...darkGray);
      doc.text('Discount:', labelX, totalY, { align: 'right' });
      doc.setTextColor(...black);
      doc.text(`-${formatCurrency(invoice.discount)}`, valueX, totalY, { align: 'right' });
    }
    
    // Tax (if any)
    if (invoice.tax > 0) {
      totalY += 6;
      doc.setTextColor(...darkGray);
      doc.text('Tax:', labelX, totalY, { align: 'right' });
      doc.setTextColor(...black);
      doc.text(formatCurrency(invoice.tax), valueX, totalY, { align: 'right' });
    }
    
    // Total
    totalY += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('Total:', labelX, totalY, { align: 'right' });
    doc.text(formatCurrency(invoice.total), valueX, totalY, { align: 'right' });
    
    // Amount due
    totalY += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text('Amount due:', labelX, totalY, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text(formatCurrency(invoice.total), valueX, totalY, { align: 'right' });

    // Save PDF
    const fileName = `Invoice-${invoice.invoiceNumber}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error generating invoice PDF:', error);
    throw new Error('Failed to generate invoice PDF');
  }
}

interface GeneratePaymentReceiptPdfParams {
  payment: PaymentOutput;
  customer?: Customer | null;
  vehicle?: Vehicle | null;
}

export function generatePaymentReceiptPdf({
  payment,
  customer,
  vehicle,
}: GeneratePaymentReceiptPdfParams): void {
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Colors
    const black = [0, 0, 0];
    const darkGray = [64, 64, 64];

    // Set font
    doc.setFont('helvetica');

    // Logo placeholder (black square in top right)
    const logoSize = 25;
    const logoX = pageWidth - margin - logoSize;
    const logoY = margin;
    doc.setFillColor(...black);
    doc.rect(logoX, logoY, logoSize, logoSize, 'F');
    
    // Pink flower icon placeholder
    doc.setFillColor(255, 192, 203); // Pink color
    const centerX = logoX + logoSize / 2;
    const centerY = logoY + logoSize / 2;
    const flowerRadius = logoSize / 6;
    doc.circle(centerX, centerY, flowerRadius, 'F');

    // Receipt title (top left)
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('Payment Receipt', margin, yPosition + 12);

    // Payment details (below title)
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    
    yPosition += 18;
    doc.text(`Payment ID: ${payment.id}`, margin, yPosition);
    
    yPosition += 6;
    if (payment.date) {
      const paymentDate = format(new Date(payment.date), 'MMMM dd, yyyy HH:mm');
      doc.text(`Date: ${paymentDate}`, margin, yPosition);
    }

    // Company information (left side, below payment details)
    yPosition += 18;
    const companyStartY = yPosition;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text(COMPANY_INFO.name, margin, yPosition);
    
    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text(COMPANY_INFO.tagline, margin, yPosition);
    
    yPosition += 6;
    doc.text(COMPANY_INFO.phone, margin, yPosition);
    
    yPosition += 6;
    doc.text(COMPANY_INFO.email, margin, yPosition);
    
    yPosition += 6;
    doc.text(COMPANY_INFO.website, margin, yPosition);

    // Customer section (right side, aligned with company info)
    const customerX = pageWidth / 2 + 5;
    yPosition = companyStartY;
    if (customer) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkGray);
      doc.text('Paid by:', customerX, yPosition);
      
      yPosition += 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...black);
      doc.text(customer.name, customerX, yPosition);
      
      yPosition += 6;
      doc.setFontSize(10);
      doc.setTextColor(...darkGray);
      if (customer.city) {
        doc.text(customer.city, customerX, yPosition);
        yPosition += 6;
      }
      if (customer.email) {
        doc.text(customer.email, customerX, yPosition);
        yPosition += 6;
      }
    }

    // Payment amount - prominent display
    yPosition += 25;
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    const amountText = formatCurrency(payment.paidAmount);
    doc.text(amountText, margin, yPosition);

    // Payment details section
    yPosition += 25;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...black);
    doc.text('Payment Details', margin, yPosition);
    
    yPosition += 12;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    // Payment Status
    doc.setTextColor(...darkGray);
    doc.text('Status:', margin, yPosition);
    doc.setTextColor(...black);
    doc.text(payment.status, margin + 30, yPosition);
    
    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text('Payment Mode:', margin, yPosition);
    doc.setTextColor(...black);
    doc.text(payment.mode, margin + 40, yPosition);
    
    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text('Reference Type:', margin, yPosition);
    doc.setTextColor(...black);
    doc.text(payment.refType, margin + 45, yPosition);
    
    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text('Reference ID:', margin, yPosition);
    doc.setTextColor(...black);
    doc.text(payment.refId, margin + 40, yPosition);
    
    if (payment.serviceType) {
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.text('Service Type:', margin, yPosition);
      doc.setTextColor(...black);
      doc.text(
        payment.serviceType === 'subscription' ? 'Subscription' : 'One-Time',
        margin + 40,
        yPosition
      );
    }
    
    // Amount breakdown
    yPosition += 12;
    doc.setTextColor(...darkGray);
    doc.text('Total Amount:', margin, yPosition);
    doc.setTextColor(...black);
    doc.text(formatCurrency(payment.totalAmount), margin + 40, yPosition);

    // Discount (only show if > 0)
    if (payment.discount > 0) {
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.text('Discount:', margin, yPosition);
      doc.setTextColor(59, 130, 246); // Blue color
      doc.text(`-${formatCurrency(payment.discount)}`, margin + 40, yPosition);
    }

    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text('Paid Amount:', margin, yPosition);
    doc.setTextColor(0, 128, 0); // Green color
    doc.text(formatCurrency(payment.paidAmount), margin + 40, yPosition);
    
    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text('Due Amount:', margin, yPosition);
    doc.setTextColor(255, 140, 0); // Orange color
    doc.text(formatCurrency(payment.dueAmount), margin + 40, yPosition);

    // Vehicle information (if available)
    if (vehicle) {
      yPosition += 15;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...black);
      doc.text('Vehicle Information', margin, yPosition);
      
      yPosition += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkGray);
      doc.text('Vehicle:', margin, yPosition);
      doc.setTextColor(...black);
      const vehicleText = vehicle.plateNumber
        ? `${vehicle.make} ${vehicle.model} - ${vehicle.plateNumber}`
        : `${vehicle.make} ${vehicle.model}`;
      doc.text(vehicleText, margin + 30, yPosition);
    }

    // Footer line
    const finalY = yPosition + 20;
    doc.setDrawColor(...darkGray);
    doc.setLineWidth(0.5);
    doc.line(margin, finalY, pageWidth - margin, finalY);
    
    yPosition = finalY + 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text('Thank you for your payment!', margin, yPosition, { align: 'center' });

    // Save PDF
    const fileName = `Payment-Receipt-${payment.id.slice(0, 8)}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error generating payment receipt PDF:', error);
    throw new Error('Failed to generate payment receipt PDF');
  }
}